import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// ── Permission groups matching the PMS Role-Permission Matrix ────────────────
const PERMISSION_GROUPS = [
  {
    label: 'Module Access',
    keys: ['dashboard', 'front_office', 'bar', 'kot', 'banquet', 'housekeeping', 'masters', 'hotel_admin']
  },
  {
    label: 'Food Inventory Roles',
    keys: ['food_admin', 'food_kitchen_staff', 'food_storekeeper', 'food_store_receive',
           'food_store_issue', 'food_store_manager', 'food_purchase_staff',
           'food_purchase_manager', 'food_cost_controller']
  },
  {
    label: 'PMS — Reservations',
    keys: ['pms_create_reservation', 'pms_modify_reservation', 'pms_cancel_reservation',
           'pms_group_booking', 'pms_rate_override']
  },
  {
    label: 'PMS — Check-In / Stay',
    keys: ['pms_checkin', 'pms_checkout', 'pms_room_move', 'pms_late_checkout']
  },
  {
    label: 'PMS — Folio & Posting',
    keys: ['pms_post_charges', 'pms_edit_posting', 'pms_void_posting',
           'pms_split_folio', 'pms_route_city_ledger']
  },
  {
    label: 'PMS — Payments',
    keys: ['pms_accept_payment', 'pms_advance_deposit', 'pms_refund', 'pms_shift_close']
  },
  {
    label: 'PMS — Night Audit & Financial Close',
    keys: ['pms_run_night_audit', 'pms_roll_business_date', 'pms_revenue_reconciliation', 'pms_view_audit_logs']
  },
  {
    label: 'PMS — Reports',
    keys: ['pms_shift_report', 'pms_occupancy_report', 'pms_revenue_report', 'pms_ar_aging', 'pms_pl_summary']
  },
  {
    label: 'PMS — System Configuration',
    keys: ['pms_rate_plan_setup', 'pms_tax_setup', 'pms_user_management', 'pms_property_setup']
  }
];

const ALL_KEYS = PERMISSION_GROUPS.flatMap((g) => g.keys);
const emptyPermissions = Object.fromEntries(ALL_KEYS.map((k) => [k, false]));

// ── Human-readable label overrides ──────────────────────────────────────────
const KEY_LABELS = {
  dashboard: 'Dashboard', bar: 'Bar Management', kot: 'KOT & POS', banquet: 'Banquet',
  front_office: 'Front Office', housekeeping: 'Housekeeping', masters: 'Masters / Config',
  hotel_admin: 'Hotel Admin (Full System)',
  food_admin: 'Food Admin', food_kitchen_staff: 'Kitchen Staff',
  food_storekeeper: 'Storekeeper', food_store_receive: 'Store Receive',
  food_store_issue: 'Store Issue', food_store_manager: 'Store Manager',
  food_purchase_staff: 'Purchase Staff', food_purchase_manager: 'Purchase Manager',
  food_cost_controller: 'Cost Controller',
  pms_create_reservation: 'Create Reservation', pms_modify_reservation: 'Modify Reservation',
  pms_cancel_reservation: 'Cancel Reservation', pms_group_booking: 'Group Booking',
  pms_rate_override: 'Rate Override',
  pms_checkin: 'Check-In', pms_checkout: 'Check-Out',
  pms_room_move: 'Room Move', pms_late_checkout: 'Late Check-Out',
  pms_post_charges: 'Post Charges', pms_edit_posting: 'Edit Posting',
  pms_void_posting: 'Void Posting', pms_split_folio: 'Split / Transfer Folio',
  pms_route_city_ledger: 'Route to City Ledger',
  pms_accept_payment: 'Accept Payment', pms_advance_deposit: 'Advance Deposit',
  pms_refund: 'Refund', pms_shift_close: 'Shift Close',
  pms_run_night_audit: 'Run Night Audit', pms_roll_business_date: 'Roll Business Date',
  pms_revenue_reconciliation: 'Revenue Reconciliation', pms_view_audit_logs: 'View Audit Logs',
  pms_shift_report: 'Shift Report', pms_occupancy_report: 'Occupancy Report',
  pms_revenue_report: 'Revenue Report', pms_ar_aging: 'AR Aging',
  pms_pl_summary: 'P&L Summary',
  pms_rate_plan_setup: 'Rate Plan Setup', pms_tax_setup: 'Tax Setup',
  pms_user_management: 'User Management', pms_property_setup: 'Property Setup'
};

// ── Standard PMS presets from the Role-Permission Matrix ────────────────────
const PMS_PRESETS = {
  '': null,
  fda: {
    role_code: 'fda', role_name: 'Front Desk Agent',
    permissions: {
      front_office: true,
      pms_create_reservation: true, pms_modify_reservation: true,
      pms_checkin: true, pms_checkout: true,
      pms_post_charges: true,
      pms_accept_payment: true, pms_advance_deposit: true,
      pms_shift_report: true
    }
  },
  cash: {
    role_code: 'cash', role_name: 'Cashier',
    permissions: {
      front_office: true,
      pms_accept_payment: true, pms_advance_deposit: true,
      pms_shift_close: true, pms_shift_report: true
    }
  },
  res: {
    role_code: 'res', role_name: 'Reservation Executive',
    permissions: {
      front_office: true,
      pms_create_reservation: true, pms_modify_reservation: true,
      pms_cancel_reservation: true, pms_group_booking: true,
      pms_advance_deposit: true, pms_shift_report: true
    }
  },
  sup: {
    role_code: 'sup', role_name: 'Front Office Supervisor',
    permissions: {
      front_office: true,
      pms_create_reservation: true, pms_modify_reservation: true,
      pms_cancel_reservation: true, pms_group_booking: true, pms_rate_override: true,
      pms_checkin: true, pms_checkout: true, pms_room_move: true, pms_late_checkout: true,
      pms_post_charges: true, pms_edit_posting: true, pms_void_posting: true,
      pms_split_folio: true, pms_route_city_ledger: true,
      pms_accept_payment: true, pms_advance_deposit: true, pms_refund: true, pms_shift_close: true,
      pms_revenue_reconciliation: true, pms_view_audit_logs: true,
      pms_shift_report: true, pms_occupancy_report: true
    }
  },
  na: {
    role_code: 'na', role_name: 'Night Auditor',
    permissions: {
      front_office: true,
      pms_post_charges: true, pms_edit_posting: true, pms_void_posting: true, pms_split_folio: true,
      pms_refund: true, pms_shift_close: true,
      pms_run_night_audit: true, pms_roll_business_date: true,
      pms_revenue_reconciliation: true, pms_view_audit_logs: true,
      pms_shift_report: true, pms_occupancy_report: true
    }
  },
  fom: {
    role_code: 'fom', role_name: 'Front Office Manager',
    permissions: {
      front_office: true,
      pms_create_reservation: true, pms_modify_reservation: true,
      pms_cancel_reservation: true, pms_group_booking: true, pms_rate_override: true,
      pms_checkin: true, pms_checkout: true, pms_room_move: true, pms_late_checkout: true,
      pms_post_charges: true, pms_edit_posting: true, pms_void_posting: true,
      pms_split_folio: true, pms_route_city_ledger: true,
      pms_accept_payment: true, pms_advance_deposit: true, pms_refund: true, pms_shift_close: true,
      pms_revenue_reconciliation: true, pms_view_audit_logs: true,
      pms_shift_report: true, pms_occupancy_report: true, pms_revenue_report: true
    }
  },
  rm: {
    role_code: 'rm', role_name: 'Revenue Manager',
    permissions: {
      front_office: true, masters: true,
      pms_create_reservation: true, pms_modify_reservation: true,
      pms_cancel_reservation: true, pms_group_booking: true, pms_rate_override: true,
      pms_occupancy_report: true, pms_revenue_report: true,
      pms_rate_plan_setup: true
    }
  },
  ar: {
    role_code: 'ar', role_name: 'City Ledger Accountant',
    permissions: {
      front_office: true,
      pms_route_city_ledger: true, pms_ar_aging: true
    }
  },
  fin: {
    role_code: 'fin', role_name: 'Finance Controller',
    permissions: {
      front_office: true, masters: true,
      pms_edit_posting: true, pms_void_posting: true, pms_route_city_ledger: true,
      pms_refund: true, pms_shift_close: true,
      pms_run_night_audit: true, pms_roll_business_date: true,
      pms_revenue_reconciliation: true, pms_view_audit_logs: true,
      pms_revenue_report: true, pms_ar_aging: true, pms_pl_summary: true,
      pms_tax_setup: true
    }
  },
  gm: {
    role_code: 'gm', role_name: 'General Manager',
    permissions: {
      front_office: true, dashboard: true,
      pms_revenue_reconciliation: true, pms_view_audit_logs: true,
      pms_occupancy_report: true, pms_revenue_report: true,
      pms_ar_aging: true, pms_pl_summary: true
    }
  },
  system_admin: {
    role_code: 'system_admin', role_name: 'System Admin (Full Control)',
    permissions: Object.fromEntries(ALL_KEYS.map((k) => [k, true]))
  }
};

const PRESET_OPTIONS = [
  { value: '', label: '— Load a preset —' },
  { value: 'fda', label: 'FDA — Front Desk Agent' },
  { value: 'cash', label: 'CASH — Cashier' },
  { value: 'res', label: 'RES — Reservation Executive' },
  { value: 'sup', label: 'SUP — Front Office Supervisor' },
  { value: 'na', label: 'NA — Night Auditor' },
  { value: 'fom', label: 'FOM — Front Office Manager' },
  { value: 'rm', label: 'RM — Revenue Manager' },
  { value: 'ar', label: 'AR — City Ledger Accountant' },
  { value: 'fin', label: 'FIN — Finance Controller' },
  { value: 'gm', label: 'GM — General Manager' },
  { value: 'system_admin', label: 'ADMIN — System Admin (Full Control)' }
];

const emptyForm = { role_code: '', role_name: '', is_active: true, permissions: { ...emptyPermissions } };

function RoleTemplateMaster({ token, selectedPropertyId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [seeding, setSeeding] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const loadTemplates = async () => {
    if (!selectedPropertyId) return setRows([]);
    try {
      const res = await fetch(`/api/role-templates?property_id=${encodeURIComponent(selectedPropertyId)}&all=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load role templates');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load role templates');
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPropertyId]);

  const resetForm = () => {
    setEditingId('');
    setForm({ ...emptyForm, permissions: { ...emptyPermissions } });
  };

  const loadPreset = (presetKey) => {
    const preset = PMS_PRESETS[presetKey];
    if (!preset) return;
    setForm({
      role_code: preset.role_code,
      role_name: preset.role_name,
      is_active: true,
      permissions: { ...emptyPermissions, ...preset.permissions }
    });
    setEditingId('');
  };

  const saveTemplate = async () => {
    setError('');
    setMessage('');
    const code = String(form.role_code || '').trim().toLowerCase();
    const name = String(form.role_name || '').trim();
    if (!selectedPropertyId) return setError('Select property first');
    if (!code) return setError('Role code required');
    if (!name) return setError('Role name required');
    try {
      const url = editingId ? `/api/role-templates/${editingId}` : '/api/role-templates';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'x-property-id': selectedPropertyId },
        body: JSON.stringify({ property_id: selectedPropertyId, role_code: code, role_name: name, is_active: form.is_active !== false, permissions_json: form.permissions })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage(editingId ? 'Template updated' : 'Template created');
      resetForm();
      loadTemplates();
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const deactivateTemplate = async (id) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/role-templates/${id}?property_id=${encodeURIComponent(selectedPropertyId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'x-property-id': selectedPropertyId }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setMessage('Template deactivated');
      loadTemplates();
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const seedDefaults = async () => {
    setError('');
    setMessage('');
    setSeeding(true);
    try {
      const res = await fetch('/api/role-templates/seed-pms-defaults', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'x-property-id': selectedPropertyId },
        body: JSON.stringify({ property_id: selectedPropertyId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Seed failed');
      setMessage(data.message || 'Defaults seeded');
      loadTemplates();
    } catch (err) {
      setError(err?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const onEdit = (row) => {
    setEditingId(String(row.id));
    setForm({
      role_code: String(row.role_code || ''),
      role_name: String(row.role_name || ''),
      is_active: row.is_active !== false,
      permissions: { ...emptyPermissions, ...(row.permissions_json || {}) }
    });
    scrollToForm();
  };

  const togglePermission = (key, value) => {
    setForm((prev) => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  const toggleGroupAll = (keys, value) => {
    setForm((prev) => {
      const updated = { ...prev.permissions };
      keys.forEach((k) => { updated[k] = value; });
      return { ...prev, permissions: updated };
    });
  };


  const permCount = Object.values(form.permissions).filter(Boolean).length;

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Role Templates</span>
      </div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--theme-primary-dark)', fontSize: '1.4rem' }}>Role Template Master</h2>
          <p style={{ margin: '0.2rem 0 0', color: '#6b7280', fontSize: '0.85rem' }}>Define reusable permission sets based on the PMS Role-Permission Matrix</p>
        </div>
        <button
          onClick={seedDefaults}
          disabled={seeding || !selectedPropertyId}
          className={seeding || !selectedPropertyId ? 'btn-muted' : 'btn-success'}
        >
          {seeding ? 'Seeding...' : 'Seed PMS Default Roles'}
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}
      {!selectedPropertyId && (
        <div className="info-banner">Select a property first from Hotel Config to manage role templates.</div>
      )}

      {/* Form */}
      <div className="card" ref={formRef} style={{ marginBottom: '1.5rem', ...editHighlightStyle(editHighlight) }}>
        <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: 'var(--theme-text)', fontSize: '1rem' }}>{editingId ? 'Edit Template' : 'Create Template'}</h3>
          {/* Preset loader */}
          <select
            onChange={(e) => loadPreset(e.target.value)}
            defaultValue=""
            style={{ minWidth: '220px' }}
          >
            {PRESET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Basic info row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Role Code</label>
            <input style={{ width: '100%', padding: '8px' }} placeholder="e.g. fda, fom, custom_role" value={form.role_code} onChange={(e) => setForm({ ...form, role_code: e.target.value })} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Role Name</label>
            <input style={{ width: '100%', padding: '8px' }} placeholder="e.g. Front Desk Agent" value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', paddingBottom: '0.1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
            <span style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: '0.5rem' }}>{permCount} permission{permCount !== 1 ? 's' : ''} selected</span>
          </div>
        </div>

        {/* Permission groups */}
        {PERMISSION_GROUPS.map((group) => {
          const groupActive = group.keys.filter((k) => form.permissions[k]).length;
          return (
            <div key={group.label} style={{ marginBottom: '0.9rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--theme-border-light)', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: '700', fontSize: '0.82rem', color: 'var(--theme-text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {group.label}
                </span>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{groupActive}/{group.keys.length}</span>
                  <button onClick={() => toggleGroupAll(group.keys, true)} className="btn-primary btn-sm" style={{ padding: '0.2rem 0.55rem' }}>All</button>
                  <button onClick={() => toggleGroupAll(group.keys, false)} className="btn-muted btn-sm" style={{ padding: '0.2rem 0.55rem' }}>None</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '0', padding: '0.5rem 0.75rem' }}>
                {group.keys.map((k) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', padding: '0.3rem 0.25rem', fontSize: '0.845rem', color: form.permissions[k] ? '#1e40af' : 'var(--theme-text)', fontWeight: form.permissions[k] ? '600' : '400' }}>
                    <input type="checkbox" checked={Boolean(form.permissions[k])} onChange={(e) => togglePermission(k, e.target.checked)} />
                    {KEY_LABELS[k] || k}
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button onClick={saveTemplate} className="btn-primary" style={{ padding: '0.55rem 1.2rem' }}>{editingId ? 'Update Template' : 'Create Template'}</button>
          <button onClick={resetForm} className="btn-muted" style={{ padding: '0.55rem 1.2rem' }}>Reset</button>
        </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
              <th style={{ padding: '0.65rem 1rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.65rem 1rem', textAlign: 'left' }}>Role Name</th>
              <th style={{ padding: '0.65rem 1rem', textAlign: 'left' }}>Permissions</th>
              <th style={{ padding: '0.65rem 1rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.65rem 1rem', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const perms = r.permissions_json || {};
              const count = Object.values(perms).filter(Boolean).length;
              const moduleKeys = ['dashboard', 'front_office', 'bar', 'kot', 'banquet', 'housekeeping', 'masters', 'hotel_admin'];
              const modules = moduleKeys.filter((k) => perms[k]).map((k) => KEY_LABELS[k] || k);
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                  <td style={{ padding: '0.6rem 1rem', fontWeight: '700', color: 'var(--theme-primary-dark)', fontFamily: 'monospace' }}>{r.role_code}</td>
                  <td style={{ padding: '0.6rem 1rem' }}>{r.role_name}</td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      {count} permission{count !== 1 ? 's' : ''}
                      {modules.length > 0 && <span style={{ marginLeft: '0.4rem', color: 'var(--theme-text)' }}>· {modules.join(', ')}</span>}
                    </div>
                  </td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <span className={r.is_active ? 'tag tag-success' : 'tag tag-danger'}>{r.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => onEdit(r)} className="btn-primary btn-sm">Edit</button>
                      {r.is_active ? <button onClick={() => deactivateTemplate(r.id)} className="btn-danger btn-sm">Deactivate</button> : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length ? <tr><td colSpan={5} style={{ padding: '2rem', color: '#9ca3af', textAlign: 'center', background: '#fafafa' }}>No templates found. Use "Seed PMS Default Roles" to create all standard roles.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RoleTemplateMaster;
